import { Refresh } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import DevicesListFilter from 'components/DevicesFilter/DevicesListFilter.js';
// @mui material components
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import Fade from '@mui/material/Fade';
import { getSelectedTimeRangeForNavDisplay } from '../../helpers/helpers.js';
import InsightsDialogSelect from './InsightsDialogSelect.js';
// import InsightsDialogSelect from "components/Dialog/InsightsDialogSelect.js";
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import DashboardLayout from 'layouts/LayoutContainers/DashboardLayout';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  fetchEfficiancyRangeDataOfType,
  setInsightsTimeRangeValue,
  clearData,
} from 'stores/sensor/heatmapDataSlice.js';
import { fetchUserDevices, setChartPopUp, setLoading } from 'stores/sensor/sensorStore';
import { TimeInMS } from 'types/time';
import DropdownMenuVertical from 'components/DropdownMenuVertical';
import { setCurrentPage } from '../../stores/sensor/sensorStore';
import FormattedChart from '../SensorPage/components/FormattedChart';
import InsightsContainer from './InsightsContainer.js';
import InsightsHeatMap from './InsightsHeatMap.js';
import CustomSnackbar from '../../components/CustomSnackbar';
import { filterDevices } from '../../helpers/filterHelpers';
import EmptyStateMessage from '../../components/MessageBox/EmptyStateMessage';
import Loading from '../../components/Loading/Loading';

function capitalizeFirstLetter(inputString) {
  if (!inputString) return inputString; // Handle empty strings or null/undefined
  return inputString.charAt(0).toUpperCase() + inputString.slice(1);
}

export const Measurements = ['co2_max', 'temperature'];

const measurementsOpts = Measurements.map(meas => {
  return {
    value: meas,
    name:
      meas !== 'co2_ratio'
        ? capitalizeFirstLetter(meas.replace(/_min|_max|_|mass_/g, ' '))
        : 'Ventilation level',
  };
});

/**
 * The refresh rate that symbolize that auto refresh is off
 */
const REFRESH_RATE_OFF = 0;

// Values in MS
const refreshRateOptions = [
  { label: 'Off', value: REFRESH_RATE_OFF },
  { label: '1 Minute', value: TimeInMS.Minute },
  { label: '5 Minutes', value: TimeInMS.Minute * 5 },
  { label: '10 Minutes', value: TimeInMS.Minute * 10 },
];

// last 12 months
const TimeRangeOptions = [
  // { label: "1 Minute", value: TimeInMS.Minute },
  { label: '1 Hour', value: TimeInMS.Hour },
  { label: 'Today', value: TimeInMS.Day },
  { label: '1 Week', value: TimeInMS.Week },
  { label: 'other', value: 'other' },
];

const REFRESH_TIME_IN_SECONDS = 300;

function Insights() {
  const [showMessage, setShowMessage] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const insightsSummaryContainer = useRef(null);
  const dispatch = useDispatch();
  // Use Redux filter state instead of useParams
  const { selectedFilterObject } = useSelector(state => state.persistence);
  const { distributor, customer, building, floor, area, hvacEquipment, status } = selectedFilterObject;
  const searchString = useSelector(state => state.persistence.searchString || '');

  const data = useSelector(state => state.heatmapData.data);
  const loading = useSelector(state => state.heatmapData.loading);
  const error = useSelector(state => state.heatmapData.error);

  // console.log('Insights data:', data);

  const chartPopUp = useSelector(state => state.heatmapData.chartPopUp);

  const insightDatesObj = useSelector(state => state.heatmapData.insightDatesObj, shallowEqual);
  const currentUser = useSelector(state => state.sensors.currentUser);
  const devices = useSelector(state => state.sensors.devices);

  const [snackbarState, setSnackbarState] = useState({
    snackbarOpen: false,
    vertical: 'top',
    horizontal: 'center',
  });
  const [measurementType, setMeasurementType] = useState('co2_max');
  const [tableWidth, setTableWidth] = useState('65vw');
  const { vertical, horizontal, snackbarOpen } = snackbarState;
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const rangeObj = useSelector(state => state.heatmapData.timeRange);

  const filteredDevices = useMemo(() => {
    const filtered = filterDevices(
      devices || [],
      {
        distributor,
        customer,
        building,
        floor,
        area,
        hvacEquipment,
        status,
        searchString,
      },
      currentUser,
    );

    return filtered;
  }, [devices, distributor, customer, building, floor, area, hvacEquipment, status, searchString]);

  useEffect(() => {
    dispatch(setCurrentPage('insights'));
  }, [dispatch]);

  useEffect(() => {
    const initializeData = async () => {
      dispatch(fetchUserDevices({ devicesType: 'Sensor' }));
      setIsInitialized(true);
    };

    initializeData();
  }, [dispatch]);

  const refreshContent = useCallback(async () => {
    if (!building || building === 'all' || filteredDevices.length === 0) {
      console.log('🟡 No valid building or devices, skipping insights API call');
      return;
    }

    dispatch(
      await fetchEfficiancyRangeDataOfType({
        forHomePage: false,
        type: measurementType,
        building_id: building,
        datesObj: insightDatesObj,
      }),
    );
  }, [measurementType, building, floor, insightDatesObj, filteredDevices, dispatch]);

  // Refresh when filters change (same as HeatMapPage)
  useEffect(() => {
    if (!isInitialized) return;

    refreshContent();
  }, [
    insightDatesObj,
    measurementType,
    distributor,
    customer,
    building,
    floor,
    filteredDevices.length,
    refreshContent,
  ]);

  useEffect(() => {
    dispatch(setLoading(false));
    const timer = setTimeout(() => {
      setShowMessage(true);
    }, 1000);

    return () => {
      clearTimeout(timer);

      dispatch(clearData());
    };
  }, [dispatch]);

  const handleSnackbarClose = () => {
    setSnackbarState({ ...snackbarState, snackbarOpen: false });
  };

  const setInsightsWindow = () => {
    if (insightsSummaryContainer.current) {
      if (
        insightsSummaryContainer.current.style.width === '35vw' ||
        insightsSummaryContainer.current.style.width === ''
      ) {
        setTableWidth('95vw');
        insightsSummaryContainer.current.style.width = '0vw';
        insightsSummaryContainer.current.style.height = '0vw';
      } else {
        setTableWidth('65vw');
        insightsSummaryContainer.current.style.width = '35vw';
        insightsSummaryContainer.current.style.height = '100%';
      }
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar objectName="Insights" />
      <Typography
        fontSize="11px"
        sx={{
          paddingLeft: { xs: 0, sm: '12px' },
          marginTop: { xs: '-15px', sm: '-5px' },
          textAlign: { xs: 'center', sm: 'left' },
        }}>
        Smart indoor air monitoring & management platform
      </Typography>
      <CustomSnackbar
        {...snackbarState}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        duration={3000}
        vertical="top"
        horizontal="left"
      />
      <Dialog open={chartPopUp !== undefined} fullWidth maxWidth="xl">
        <DialogTitle sx={{ padding: '0' }}>
          <IconButton aria-label="close" onClick={() => dispatch(setChartPopUp(undefined))}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ paddingBottom: '50px' }}>
          {chartPopUp ? (
            <FormattedChart
              title={chartPopUp?.title}
              datasets={chartPopUp?.datasets}
              isRange={chartPopUp?.isRange}
              setChartPopUp={setChartPopUp}
              isPopUp={true}
            />
          ) : undefined}
        </DialogContent>
      </Dialog>
      <MDBox pt={{ xs: 7, sm: 1 }} pb={1}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <MDBox
              mb={1}
              px={2}
              variant="gradient"
              borderRadius="lg"
              coloredShadow="light"
              id="topBar"
              sx={{
                position: 'relative',
                zIndex: 1200,
              }}
              style={{
                background: '#DEE4F9',
              }}>
              <Box
                sx={{
                  display: { xs: 'flex', sm: 'grid' },
                  gridTemplateColumns: { sm: '1fr auto 1fr' },
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  gap: { xs: 1, sm: 0 },
                }}>
                <Stack
                  direction="row"
                  marginLeft={2}
                  spacing={2}
                  alignItems="end"
                  className="top-bar"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: {
                      xs: '100%',
                      lg: 'fit-content',
                    },
                  }}>
                  <MDTypography variant="h4" color="dark">
                    {measurementType === 'co2_max'
                      ? 'Fresh Air Insights'
                      : 'HVAC: Cooling & Heating Insights'}
                  </MDTypography>
                </Stack>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: { xs: '100%', sm: 'auto' },
                  }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'stretch', sm: 'center' },
                      justifyContent: 'center',
                      gap: 1,
                      width: { xs: '100%', sm: 'auto' },
                    }}>
                    <DevicesListFilter
                      status={false}
                      search={false}
                      hvacEquipment
                      extraDropdownItems={
                        <DropdownMenuVertical
                          labelName="Measurement"
                          options={measurementsOpts}
                          selectedOption={measurementType}
                          handleChange={e => {
                            setMeasurementType(e.target.value);
                          }}
                          width="100%"
                        />
                      }
                    />
                  </Box>
                </Box>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    width: { xs: '100%', sm: 'auto' },
                  }}>
                  <InsightsDialogSelect
                    buttonTitle="Time Range"
                    dialogTitle="Choose Time Range"
                    buttonSelectedTimeRangeForDisplay={getSelectedTimeRangeForNavDisplay(
                      insightDatesObj.startDate,
                      insightDatesObj.endDate,
                    )}
                    color="black"
                    selectLabel="Time Range"
                    selectedStartDate={insightDatesObj.startDate}
                    selectedEndDate={insightDatesObj.endDate}
                    onSelectionOK={setInsightsTimeRangeValue}
                    onClose={refreshContent}
                    toolTip="You can select up to seven days range."></InsightsDialogSelect>

                  <IconButton
                    color="primary" // ✅ FIXED: Use valid Material-UI color
                    onClick={() => refreshContent()}>
                    <Refresh />
                  </IconButton>
                </Stack>
              </Box>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <MDBox
        sx={{
          overflowX: 'visible',
          overflowY: 'visible',
          display: 'flex',
          height: '100%',
          flexDirection: {
            xs: 'column',
            lg: 'row',
          },
          justifyContent: 'space-between',
        }}
        className="bodycontainer">
        <Box
          id="tablezzz"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginTop: '-.5rem',
            flexGrow: '1',
            width: {
              xs: '100vw',
              lg: '65vw',
            },
          }}>
          {building === 'all' ? (
            <EmptyStateMessage
              message="Please select a building to view insights"
              maxHeight="10rem"
              width="30rem"
            />
          ) : filteredDevices.length === 0 ? (
            <EmptyStateMessage
              message="No sensors found with current filters"
              maxHeight="10rem"
              width="30rem"
            />
          ) : loading ? (
            <Box
              sx={{
                height: '50vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Loading />
            </Box>
          ) : error ? (
            <EmptyStateMessage
              message={`Error loading insights: ${error}`}
              maxHeight="10rem"
              width="30rem"
            />
          ) : (
            <>
              {/* ✅ FIXED: Defensive validation like HeatMapPage */}
              {Array.isArray(data?.sensorsTypeMeasurements) &&
              data.sensorsTypeMeasurements.length > 0 ? (
                <InsightsHeatMap
                  type={measurementType}
                  timeRangeObj={rangeObj}
                  sensorsList={data.sensorsTypeMeasurements}
                  width={tableWidth}
                />
              ) : (
                <Fade in={showMessage} timeout={500}>
                  <Typography
                    sx={{
                      height: '50vh',
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    No insights data available for the selected sensors and time range.
                  </Typography>
                </Fade>
              )}
            </>
          )}
        </Box>

        {Array.isArray(data?.sensorsTypeMeasurements) &&
          data.sensorsTypeMeasurements.length > 0 && (
            <Box
              sx={
                {
                  // border: '2px solid black',
                }
              }>
              <IconButton aria-label="close" onClick={() => setInsightsWindow(undefined)}>
                {tableWidth === '65vw' ? <KeyboardArrowRightIcon /> : <MenuOpenIcon />}
              </IconButton>
            </Box>
          )}
        {building !== 'all' &&
          Array.isArray(data?.sensorsTypeMeasurements) &&
          data.sensorsTypeMeasurements.length > 0 && (
            <Box
              ref={insightsSummaryContainer}
              sx={{
                padding: 0.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                width: '35vw',
                overflow: 'hidden',
              }}>
              <Box
                sx={{
                  overflow: 'hidden',
                  padding: 0.5,
                }}>
                <InsightsContainer rangeObj={rangeObj} />
              </Box>
            </Box>
          )}
      </MDBox>
    </DashboardLayout>
  );
}

export default Insights;
